/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, X } from "lucide-react";
import getMediaUrl from "../tools/utils.jsx";

const defaultNormalizeResults = (data) => data?.results ?? data ?? [];
const defaultParamsForQuery = (query) => ({ search: query || undefined });

export default function SearchPicker({
    label,
    value,
    selectedLabel,
    endpoint,
    placeholder = "Buscar",
    emptyText = "Sin resultados",
    minQueryLength = 0,
    paramsForQuery = defaultParamsForQuery,
    normalizeResults = defaultNormalizeResults,
    getOptionLabel = (option) => option?.name || `#${option?.id}`,
    getOptionDescription,
    getOptionThumbnail,
    onSelect,
    disabled = false,
}) {
    const [query, setQuery] = useState(selectedLabel || (value ? `#${value}` : ""));
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [options, setOptions] = useState([]);

    const canSearch = useMemo(
        () => String(query || "").trim().length >= minQueryLength,
        [query, minQueryLength]
    );

    useEffect(() => {
        if (!open) {
            setQuery(selectedLabel || (value ? `#${value}` : ""));
        }
    }, [open, selectedLabel, value]);

    useEffect(() => {
        if (!open || disabled || !canSearch) {
            setOptions([]);
            return undefined;
        }

        const timeout = setTimeout(async () => {
            setLoading(true);
            setError("");
            try {
                const { data } = await axios.get(endpoint, {
                    params: paramsForQuery(String(query || "").trim()),
                });
                const next = normalizeResults(data);
                setOptions(Array.isArray(next) ? next : []);
            } catch (err) {
                console.error("Search picker error:", err);
                setError("No se pudo buscar.");
            } finally {
                setLoading(false);
            }
        }, 250);

        return () => clearTimeout(timeout);
    }, [canSearch, disabled, endpoint, normalizeResults, open, paramsForQuery, query]);

    const clearSelection = () => {
        setQuery("");
        setOptions([]);
        onSelect?.(null);
        setOpen(true);
    };

    const chooseOption = (option) => {
        const nextLabel = getOptionLabel(option);
        setQuery(nextLabel);
        setOpen(false);
        onSelect?.(option);
    };

    return (
        <div className="form-control relative">
            {label && (
                <label className="label py-0.5">
                    <span className="label-text text-xs font-semibold">{label}</span>
                </label>
            )}
            <label className={`input input-bordered flex items-center gap-2 ${disabled ? "input-disabled" : ""}`}>
                <Search className="w-4 h-4 opacity-50" />
                <input
                    className="grow min-w-0"
                    value={query}
                    disabled={disabled}
                    placeholder={placeholder}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setOpen(true);
                    }}
                />
                {value && !disabled && (
                    <button type="button" className="btn btn-xs btn-ghost btn-circle" onMouseDown={(e) => e.preventDefault()} onClick={clearSelection}>
                        <X className="w-3 h-3" />
                    </button>
                )}
            </label>

            {open && !disabled && (
                <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-lg border border-base-300 bg-base-100 shadow-xl max-h-72 overflow-auto">
                    {loading ? (
                        <div className="p-3 text-sm text-base-content/60 flex items-center gap-2">
                            <span className="loading loading-spinner loading-xs" />
                            Buscando
                        </div>
                    ) : error ? (
                        <div className="p-3 text-sm text-error">{error}</div>
                    ) : !canSearch ? (
                        <div className="p-3 text-sm text-base-content/50">Escribe para buscar.</div>
                    ) : options.length === 0 ? (
                        <div className="p-3 text-sm text-base-content/50">{emptyText}</div>
                    ) : (
                        <ul className="menu menu-sm p-1">
                            {options.map((option) => {
                                const thumbnail = getOptionThumbnail?.(option);
                                return (
                                    <li key={option.id}>
                                        <button type="button" className="flex items-center gap-3" onMouseDown={(e) => e.preventDefault()} onClick={() => chooseOption(option)}>
                                            {thumbnail ? (
                                                <img src={getMediaUrl(thumbnail)} alt="" className="w-9 h-9 rounded-md object-cover bg-base-200" />
                                            ) : (
                                                <span className="w-9 h-9 rounded-md bg-base-200 flex items-center justify-center text-xs text-base-content/50">
                                                    #{option.id}
                                                </span>
                                            )}
                                            <span className="min-w-0 flex-1 text-left">
                                                <span className="block truncate font-medium">{getOptionLabel(option)}</span>
                                                {getOptionDescription && (
                                                    <span className="block truncate text-xs text-base-content/55">
                                                        {getOptionDescription(option)}
                                                    </span>
                                                )}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
